// Background selection module

/**
 * Background options configuration
 */
const backgrounds = [
    {
        id: 'cyber_schooling',
        image: '/backgrounds/cyber_schooling.png',
        translationKey: 'settings.uiSettings.backgrounds.cyberSchooling'
    },
    {
        id: 'focus',
        image: '/backgrounds/focus.png',
        translationKey: 'settings.uiSettings.backgrounds.focus'
    },
    {
        id: 'concentration',
        image: '/backgrounds/concentration.png',
        translationKey: 'settings.uiSettings.backgrounds.concentration'
    },
    {
        id: 'relaxation',
        image: '/backgrounds/relaxation.png',
        translationKey: 'settings.uiSettings.backgrounds.relaxation'
    },
    {
        id: 'lianas',
        image: '/backgrounds/lianas.png',
        translationKey: 'settings.uiSettings.backgrounds.lianas'
    },
    {
        id: 'color',
        type: 'color',
        translationKey: 'settings.uiSettings.backgrounds.color'
    }
];

// Preset colors (8 dark, 8 light)
const backgroundColors = [
    // Dark
    { color: '#121212', translationKey: 'settings.uiSettings.backgroundColors.deepDark' },
    { color: '#1e1e1e', translationKey: 'settings.uiSettings.backgroundColors.darkGrey' },
    { color: '#2c3e50', translationKey: 'settings.uiSettings.backgroundColors.midnightBlue' },
    { color: '#1A237E', translationKey: 'settings.uiSettings.backgroundColors.indigo' },
    { color: '#004D40', translationKey: 'settings.uiSettings.backgroundColors.darkTeal' },
    { color: '#3E2723', translationKey: 'settings.uiSettings.backgroundColors.brown' },
    { color: '#B71C1C', translationKey: 'settings.uiSettings.backgroundColors.darkRed' },
    { color: '#4A148C', translationKey: 'settings.uiSettings.backgroundColors.deepPurple' },

    // Light
    { color: '#ffffff', translationKey: 'settings.uiSettings.backgroundColors.pureWhite' },
    { color: '#f5f6fa', translationKey: 'settings.uiSettings.backgroundColors.softWhite' },
    { color: '#dcdde1', translationKey: 'settings.uiSettings.backgroundColors.lightGrey' },
    { color: '#e3f2fd', translationKey: 'settings.uiSettings.backgroundColors.paleBlue' },
    { color: '#e8f5e9', translationKey: 'settings.uiSettings.backgroundColors.paleGreen' },
    { color: '#fff3e0', translationKey: 'settings.uiSettings.backgroundColors.paleOrange' },
    { color: '#f3e5f5', translationKey: 'settings.uiSettings.backgroundColors.lavender' },
    { color: '#eceff1', translationKey: 'settings.uiSettings.backgroundColors.blueGrey' }
];

/**
 * Initialize the background picker UI
 */
export function initBackgroundPicker() {
    const grid = document.getElementById('backgroundPickerGrid');
    if (!grid) return;

    // Load saved background preference
    const savedBackground = localStorage.getItem('backgroundPreference') || 'cyber_schooling';

    grid.innerHTML = '';
    backgrounds.forEach(bg => {
        const option = createBackgroundOption(bg, savedBackground);
        grid.appendChild(option);
    });

    // Initialize the color picker modal
    initBackgroundColorPickerElement();

    // Apply the saved background
    applyBackground(savedBackground);
}

/**
 * Initialize the background color picker modal elements
 */
function initBackgroundColorPickerElement() {
    const modal = document.getElementById('backgroundColorPickerModal');
    const closeBtn = document.getElementById('closeBackgroundColorPicker');
    const grid = document.getElementById('backgroundColorPickerGrid');

    if (!modal || !grid) return;

    // Close button handler
    if (closeBtn) {
        // Clone to assume no duplicates if run multiple times, or just add event listener?
        // Better: remove old listener by cloning if needed, but for now simple add
        closeBtn.replaceWith(closeBtn.cloneNode(true));
        document.getElementById('closeBackgroundColorPicker').addEventListener('click', () => {
            modal.style.display = 'none';
        });
    }

    // Populate grid
    grid.innerHTML = '';
    backgroundColors.forEach(bg => {
        const circle = document.createElement('div');
        circle.className = 'color-preset';
        circle.style.backgroundColor = bg.color;

        // Translate name
        const name = window.i18n ? window.i18n.t(bg.translationKey) : bg.translationKey;
        circle.title = name;

        circle.addEventListener('click', () => {
            selectBackgroundColor(bg.color);
            modal.style.display = 'none';
        });

        circle.addEventListener('mouseover', () => {
            const display = document.getElementById('backgroundColorNameDisplay');
            if (display) {
                // Re-fetch translation in case language changed
                display.textContent = window.i18n ? window.i18n.t(bg.translationKey) : bg.translationKey;
            }
        });

        grid.appendChild(circle);
    });

    // Close on click outside
    window.addEventListener('click', (e) => {
        if (e.target === modal) {
            modal.style.display = 'none';
        }
    });
}
/**
 * Create a background option element
 */
function createBackgroundOption(background, selectedId) {
    const option = document.createElement('div');
    option.className = 'background-option';
    option.dataset.backgroundId = background.id;

    if (background.id === selectedId) {
        option.classList.add('active');
    }

    if (background.type === 'color') {
        // Color option - gradient with icon
        option.classList.add('color-option');
        option.innerHTML = `
            <div class="color-picker-icon">🎨</div>
            <div class="background-label" data-i18n="${background.translationKey}">
                ${window.i18n ? window.i18n.t(background.translationKey) : background.id}
            </div>
        `;
    } else {
        // Image background option
        option.innerHTML = `
            <img src="${background.image}" alt="${background.id}" class="background-preview">
            <div class="background-label" data-i18n="${background.translationKey}">
                ${window.i18n ? window.i18n.t(background.translationKey) : background.id}
            </div>
        `;
    }

    option.addEventListener('click', () => {
        selectBackground(background.id);
    });

    return option;
}

/**
 * Select and apply a background mode
 */
function selectBackground(backgroundId) {
    // Update UI selection
    const allOptions = document.querySelectorAll('.background-option');
    allOptions.forEach(opt => {
        opt.classList.toggle('active', opt.dataset.backgroundId === backgroundId);
    });

    // Save preference
    localStorage.setItem('backgroundPreference', backgroundId);

    // If color mode selected, open the picker
    if (backgroundId === 'color') {
        const modal = document.getElementById('backgroundColorPickerModal');
        if (modal) {
            modal.style.display = 'flex';
        }
    }

    // Apply the background
    applyBackground(backgroundId);
}

/**
 * Select a specific solid color
 */
function selectBackgroundColor(color) {
    localStorage.setItem('backgroundColorValue', color);

    // Also ensure we are in 'color' mode
    selectBackground('color');

    // Apply immediately (redundant with selectBackground but safe)
    applyBackground('color');
}

/**
 * Apply the selected background to the body
 */
function applyBackground(backgroundId) {
    const body = document.body;

    if (backgroundId === 'color') {
        const savedColor = localStorage.getItem('backgroundColorValue');

        body.style.backgroundImage = '';
        if (savedColor) {
            body.style.background = savedColor;
        } else {
            // Default gradient if no specific color chosen yet
            body.style.background = 'linear-gradient(135deg, var(--bg-dark) 0%, var(--bg-medium) 100%)';
        }
    } else {
        // Find the background configuration
        const bg = backgrounds.find(b => b.id === backgroundId);
        if (bg && bg.image) {
            body.style.backgroundImage = `url('${bg.image}')`;
            body.style.backgroundSize = 'cover';
            body.style.backgroundPosition = 'center calc(50% - 20px)';
            body.style.backgroundRepeat = 'no-repeat';
            body.style.backgroundAttachment = 'fixed';
        }
    }
}

/**
 * Update translations for all background labels
 */
export function updateBackgroundTranslations() {
    const labels = document.querySelectorAll('.background-label[data-i18n]');
    labels.forEach(label => {
        const key = label.getAttribute('data-i18n');
        if (key && window.i18n) {
            label.textContent = window.i18n.t(key);
        }
    });
}
